// Utility functions for schedule app
export function toMinutes(t) {
  if (!t) return 0;
  const [time, ampm] = t.split(" ");
  let [h, m] = time.split(":").map(Number);
  if (ampm === "PM" && h < 12) h += 12;
  if (ampm === "AM" && h === 12) h = 0;
  return h * 60 + m;
}

export function formatTime(t) {
  if (!t) return "";
  const [hourStr, minuteStr] = t.split(":");
  let h = parseInt(hourStr, 10);
  const m = parseInt(minuteStr || '0', 10);
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  const minutePart = m === 0 ? '' : `:${m.toString().padStart(2, '0')}`;
  return `${h}${minutePart}${ampm}`;
}

export function getMonday(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = (day + 6) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

// Get the week start date for a given date according to a named start day
export function getWeekStart(date, startDay = 'Monday') {
  const dayMap = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6
  };
  const target = dayMap[startDay] ?? 1;
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  // compute how many days we are past the target start
  const diff = (day - target + 7) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

export function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

// Timeline utilities
export function timeToMinutes(timeStr) {
  // Convert time string to minutes from midnight.
  // Accepts both "HH:MM" (24h) and various AM/PM formats like "11:45am", "11:45 am", or "11:45 AM".
  if (!timeStr) return 0;
  const s = String(timeStr).trim();
  // If contains am/pm (case-insensitive), normalize and delegate to toMinutes
  if (/\b(am|pm)\b/i.test(s) || /[ap]m$/i.test(s)) {
    // Ensure there's a space before AM/PM and uppercase it: "11:45am" -> "11:45 AM"
    const normalized = s.replace(/\s*/g, '').replace(/([ap]m)$/i, (m) => ' ' + m.toUpperCase());
    return toMinutes(normalized);
  }
  const parts = s.split(':').map(Number);
  const hours = parts[0] || 0;
  const minutes = parts[1] || 0;
  return hours * 60 + minutes;
}

export function minutesToTime(minutes) {
  // Convert minutes from midnight to "HH:MM" format
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

export function getTimelinePosition(startTime, totalMinutes = 1440, containerWidth = 1000, rangeStartMinutes = 0) {
  // Convert time to pixel position on timeline
  // Use toMinutes for "HH:MM AM/PM" format or timeToMinutes for "HH:MM" format
  let minutes;
  if (typeof startTime === 'string' && /\b(am|pm)\b/i.test(startTime)) {
    minutes = toMinutes(startTime.replace(/\s*/g, '').replace(/([ap]m)$/i, (m) => ' ' + m.toUpperCase()));
  } else {
    minutes = timeToMinutes(startTime);
  }
  
  // Adjust for the visible time range
  const adjustedMinutes = minutes - rangeStartMinutes;
  return (adjustedMinutes / totalMinutes) * containerWidth;
}

export function getTimelineWidth(startTime, endTime, totalMinutes = 1440, containerWidth = 1000, rangeStartMinutes = 0) {
  // Get the width of a shift bar on timeline
  let startMinutes, endMinutes;
  if (typeof startTime === 'string' && /\b(am|pm)\b/i.test(startTime)) {
    startMinutes = toMinutes(String(startTime).replace(/\s*/g, '').replace(/([ap]m)$/i, (m) => ' ' + m.toUpperCase()));
    endMinutes = toMinutes(String(endTime).replace(/\s*/g, '').replace(/([ap]m)$/i, (m) => ' ' + m.toUpperCase()));
  } else {
    startMinutes = timeToMinutes(startTime);
    endMinutes = timeToMinutes(endTime);
  }
  
  const duration = endMinutes - startMinutes;
  return (duration / totalMinutes) * containerWidth;
}

export function pixelToTime(pixelPosition, totalMinutes = 1440, containerWidth = 1000) {
  // Convert pixel position back to time
  const minutes = (pixelPosition / containerWidth) * totalMinutes;
  return minutesToTime(Math.round(minutes / 15) * 15); // Round to 15-minute intervals
}

export function getTimelineMarkers(interval = 4, startHour = 0, endHour = 24) {
  // Generate time markers for timeline header (every N hours within visible range)
  const markers = [];
  for (let hour = startHour; hour < endHour; hour += interval) {
    const time = `${hour.toString().padStart(2, '0')}:00`;
    const label = formatTime(time);
    markers.push({ time, label, minutes: hour * 60 });
  }
  return markers;
}

// Format time to 12-hour format (alias for formatTime)
export function formatTime12Hour(time) {
  return formatTime(time);
}
